// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { addLineCollection } from "@insite/client-framework/Services/CartService";
import { getQuote } from "@insite/client-framework/Services/QuoteService";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { CartLineCollectionModel, CartLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";

type RequoteParameter = {
    quoteId: string;
} & HasOnSuccess &
    HasOnError<string>;

export interface RequoteResult {
    quote: QuoteModel;
    cartLines?: CartLineCollectionModel;
}

type HandlerType = ApiHandlerDiscreteParameter<RequoteParameter, CartLineCollectionModel, RequoteResult>;

export const DispatchBeginRequote: HandlerType = props => {
    props.dispatch({
        type: "Context/BeginUpdatingCart",
    });
};

export const FetchQuoteWithLines: HandlerType = async props => {
    try {
        let quote = getQuoteState(props.getState(), props.parameter.quoteId).value;
        if (!quote?.quoteLineCollection) {
            quote = await getQuote(props.parameter);
            props.dispatch({
                type: "Data/Quotes/CompleteLoadQuote",
                quote,
            });
        }
        props.apiResult = { quote } as any;
    } catch (error) {
        if ("status" in error && (error.status === 400 || error.status === 404)) {
            props.dispatch({
                type: "Data/Quotes/FailedToLoadQuote",
                quoteId: props.parameter.quoteId,
                status: 404,
            });
            return false;
        }
        throw error;
    }
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        cartLines: props?.apiResult.quote?.quoteLineCollection?.map(
            line =>
                ({
                    productId: line.productId,
                    qtyOrdered: line?.qtyOrdered && line.qtyOrdered <= 0 ? 1 : line.qtyOrdered,
                    unitOfMeasure: line.unitOfMeasure,
                } as CartLineModel),
        ),
    } as CartLineCollectionModel;
};

export const SendDataToApi: HandlerType = async props => {
    if (!props.apiResult) {
        return;
    }
    props.apiResult.cartLines = await addLineCollection({
        cartId: API_URL_CURRENT_FRAGMENT,
        cartLineCollection: props.apiParameter as any,
    });
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.();
};

export const LoadCart: HandlerType = props => {
    props.dispatch(loadCurrentCart());
};

export const DispatchCompleteRequote: HandlerType = props => {
    props.dispatch({
        type: "Context/CompleteUpdatingCart",
    });
};
export const chain = [
    DispatchBeginRequote,
    FetchQuoteWithLines,
    PopulateApiParameter,
    SendDataToApi,
    ExecuteOnSuccessCallback,
    LoadCart,
    DispatchCompleteRequote,
];

const requote = createHandlerChainRunner(chain, "requote");
export default requote;
